import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import {
  DemandEntriesQuery,
  DemandEntriesResponse,
  DemandRefreshAccepted,
  DemandRefreshDetail,
  DemandRefreshIdParam,
  DemandRefreshListQuery,
  DemandRefreshListResponse,
  DemandRefreshRequest,
  ForecastConsumption,
  ForecastConsumptionQuery,
  ForecastConflictProblem,
  ForecastCreateRequest,
  ForecastDetail,
  ForecastDetailQuery,
  ForecastIdParam,
  ForecastLinesInvalidProblem,
  ForecastLinesPutRequest,
  ForecastListQuery,
  ForecastListResponse,
  ForecastPatchRequest,
  Forecast,
  MpsApproveForbiddenProblem,
  MpsApproveRequest,
  MpsLineConflictProblem,
  MpsPlanStatusProblem,
  MpsProposeConflictProblem,
  MpsLineParams,
  MpsOverrideRequest,
  MpsOverrideResponse,
  MpsPlan,
  MpsPlanDetail,
  MpsPlanDetailQuery,
  MpsPlanIdParam,
  MpsPlanListQuery,
  MpsPlanListResponse,
  MpsProposeRequest,
  MpsRejectRequest,
  MpsSubmitRequest,
  PlanningFacilityQuery,
  PlanningParameters,
  PlanningParametersConflictProblem,
  PlanningParametersInvalidProblem,
  PlanningParametersListQuery,
  PlanningParametersListResponse,
  PlanningParametersPutRequest,
  PlanningProductParam,
  PlanningSettings,
  PlanningSettingsConflictProblem,
  PlanningSettingsForbiddenProblem,
  PlanningSettingsPutRequest,
  SafetyStockConflictProblem,
  SafetyStockInvalidProblem,
  SafetyStockPolicy,
  SafetyStockPolicyListQuery,
  SafetyStockPolicyListResponse,
  SafetyStockPolicyPutRequest,
} from "./schemas.js";
import {
  CalendarException,
  CalendarExceptionCreateRequest,
  CalendarExceptionIdParam,
  CalendarExceptionInvalidProblem,
  CalendarExceptionListQuery,
  CalendarExceptionListResponse,
  FacilityCalendarPreview,
  FacilityCalendarPreviewQuery,
  ShiftPattern,
  ShiftPatternConflictProblem,
  ShiftPatternCreateRequest,
  ShiftPatternForbiddenProblem,
  ShiftPatternIdParam,
  ShiftPatternInvalidProblem,
  ShiftPatternListQuery,
  ShiftPatternListResponse,
  ShiftPatternPatchRequest,
  WorkCenterCalendar,
  WorkCenterCalendarConflictProblem,
  WorkCenterCalendarCreateRequest,
  WorkCenterCalendarIdParam,
  WorkCenterCalendarListQuery,
  WorkCenterCalendarListResponse,
  WorkCenterCalendarPatchRequest,
} from "./calendar-schemas.js";
import {
  ActionMessageAcceptRequest,
  ActionMessageBulkAcceptRequest,
  ActionMessageConflictProblem,
  ActionMessageDismissRequest,
  MrpGroupPassAccepted,
  MrpGroupPassConflictProblem,
  MrpGroupPassRequest,
  MrpRun,
  MrpRunAccepted,
  MrpRunIdParam,
  MrpRunListQuery,
  MrpRunListResponse,
  MrpRunRequest,
  PlannedOrder,
  PlannedOrderBulkResponse,
  PlannedOrderCancelRequest,
  PlannedOrderConvertRequest,
  PlannedOrderConvertResponse,
  PlannedOrderDetail,
  PlannedOrderFirmRequest,
  PlannedOrderIdParam,
  PlannedOrderListQuery,
  PlannedOrderListResponse,
  PlannedOrderStatusProblem,
  PlannedOrderSummary,
} from "./mrp-schemas.js";
import {
  CapacityLoad,
  CapacityLoadQuery,
  ScheduleApplyMoveForbiddenProblem,
  ScheduleApplyMoveRequest,
  ScheduleBoard,
  ScheduleBoardQuery,
  ScheduleConflictProblem,
  ScheduleDecisionRequest,
  ScheduleDiff,
  ScheduleDirectiveConflictProblem,
  ScheduleDirectiveIdParam,
  ScheduleDirectiveListQuery,
  ScheduleDirectiveListResponse,
  ScheduleHeader,
  ScheduleIdParam,
  ScheduleListQuery,
  ScheduleListResponse,
  ScheduleLiveQuery,
  ScheduleMoveBreaksPrecedenceProblem,
  ScheduleMoveCommitRequest,
  ScheduleMoveNotApplicableProblem,
  ScheduleMovePreview,
  ScheduleMovePreviewRequest,
  ScheduleMoveResult,
  ScheduleOverloadListQuery,
  ScheduleOverloadListResponse,
  ScheduleOverloadParams,
  SchedulePreviewStaleProblem,
  ScheduleRun,
  ScheduleRunIdParam,
  ScheduleRunListQuery,
  ScheduleRunListResponse,
  ScheduleSolveAccepted,
  ScheduleSolveRequest,
  SetupMatrix,
  SetupMatrixConflictProblem,
  SetupMatrixInvalidProblem,
  SetupMatrixPutRequest,
  SetupMatrixExplainQuery,
  SetupMatrixExplanation,
  SetupMatrixQuery,
  SetupMatrixWorkCenterParam,
} from "./schedule-schemas.js";
import {
  CtpCommitConflictProblem,
  CtpCommitRequest,
  CtpPromise,
  CtpPromiseDetail,
  CtpPromiseIdParam,
  CtpPromiseListQuery,
  CtpPromiseListResponse,
  CtpQuoteRequest,
  CtpQuoteResult,
  CtpUnavailableProblem,
  CtpWithdrawConflictProblem,
  CtpWithdrawRequest,
} from "./promise-schemas.js";
import {
  Scenario,
  ScenarioActionRequest,
  ScenarioCreateAccepted,
  ScenarioCreateRequest,
  ScenarioIdParam,
  ScenarioListQuery,
  ScenarioListResponse,
  ScenarioProblem,
  ScenarioPromoteResult,
} from "./scenario-schemas.js";

// Module 03 planning: G-1's eleven master-data routes, G-2's five demand pool
// routes, G-3's seven MPS routes, P3-H-1's eleven calendar routes, P3-H-2's
// twelve MRP routes, P3-H-3's conversion route, P3-I-2's fourteen setup
// matrix and schedule routes and P3-I-3's five move, drag and directive
// routes (spec 03 §5 rows 1 to 16, 18 to 24, 26 to 50, 54 to 71 and 54a;
// D-349 to D-351, D-386 to D-390, D-397 to D-406, D-408). Every mutation takes an Idempotency-Key and a replay is
// served to its own principal only (D-303). Every route is facility-scoped: a
// facility role outside its facility gets 404 for a row and 403 for a facility
// parameter. Refusals are listed in the order the door meets them.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "put" | "patch" | "delete";
  path: string;
  operationId: string;
  summary: string;
  description: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
}): void {
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["planning"],
    summary: config.summary,
    description: config.description,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(config.method !== "get" ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const json = <T>(description: string, schema: T) => ({
  description,
  content: { "application/json": { schema } },
});
const problemOf = <T>(description: string, schema: T) => ({
  description,
  content: { "application/problem+json": { schema } },
});

const FACILITY_DOOR =
  "The facility door: 400 `VALIDATION_FAILED` without `facility_id`, 403 `PERMISSION_DENIED` for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND` when no active facility of the tenant has that id.";

// --- settings (rows 1 and 2) ------------------------------------------------------

route({
  method: "get",
  path: "/v1/planning/settings",
  operationId: "getPlanningSettings",
  summary: "Read a facility's planning settings",
  description:
    "Spec 03 §2.22 and §5 row 1. Gated by `planning.read`. Writes nothing: a facility whose row was never written reads the column defaults with `updated_at` null. " +
    FACILITY_DOOR,
  request: { query: PlanningFacilityQuery },
  responses: {
    200: json("The facility's settings", PlanningSettings),
    400: problemResponse("VALIDATION_FAILED: `facility_id` is missing or not a uuid"),
    403: problemResponse(
      "PERMISSION_DENIED: no `planning.read`, or the facility is outside the caller's scope",
    ),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/planning/settings",
  operationId: "putPlanningSettings",
  summary: "Save a facility's planning settings",
  description:
    "Spec 03 §2.22 and §5 row 2. Gated by `planning.settings.manage`. The body carries every editable column and `expected_updated_at`; `null` inserts the facility's first row. `demand_refresh_debounce_minutes`, `facility_shift_pattern_id` (P3-H-1), the two MRP fields (P3-H-2), and the five schedule fields `schedule_horizon_days`, `schedule_batching_window_hours`, `schedule_solve_debounce_minutes`, `schedule_auto_publish` and `schedule_frozen_hours` (P3-I-2, §5 row 72), and `overtime_cost_pct` (P3-I-3, D-402 ruling 2) are optional, and an omitted value keeps the stored one; a null pattern clears the facility's calendar. The worker watermarks, `nightly_solved_on` among them, are read-only. The agent firm cap is no longer a field: it moved to the planner agent's policy (D-526 ruling 2), and a body that still names it is refused 400. Refusals in order: 403 without the code, then " +
    FACILITY_DOOR +
    " Then 400 for a field out of its CHECK, an auto-approve bar above the facility-admin bar (both fields named), or a field the body may not carry, such as a worker watermark. Then 404 `SHIFT_PATTERN_NOT_FOUND` for a `facility_shift_pattern_id` that names no active pattern (§5 row 37). Then 409 `PLANNING_SETTINGS_CHANGED` with the stored row as `current`. Then 403 `PERMISSION_DENIED` naming in `fields` each MPS bar the body changes, with `required_role_lineage: group_admin`, for a caller whose lineage root is not group_admin (D-351 ruling 8); nothing is written. Appends `planning_settings.updated` naming the changed fields.",
  request: {
    query: PlanningFacilityQuery,
    body: { content: { "application/json": { schema: PlanningSettingsPutRequest } } },
  },
  responses: {
    200: json("The saved settings", PlanningSettings),
    400: problemResponse(
      "VALIDATION_FAILED: no `facility_id`, a field out of its CHECK, the auto-approve bar above the facility-admin bar, or an unknown field / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemOf(
      "PERMISSION_DENIED: no `planning.settings.manage`; a facility outside scope; or an MPS bar changed by a caller outside the group_admin lineage, named in `fields`",
      PlanningSettingsForbiddenProblem,
    ),
    404: problemResponse("FACILITY_NOT_FOUND / SHIFT_PATTERN_NOT_FOUND"),
    409: problemOf(
      "PLANNING_SETTINGS_CHANGED: `expected_updated_at` is stale; `current` holds the stored row",
      PlanningSettingsConflictProblem,
    ),
  },
});

// --- parameters (rows 3 and 4) ------------------------------------------------------

route({
  method: "get",
  path: "/v1/planning/parameters",
  operationId: "listPlanningParameters",
  summary: "List the planning parameters of a facility",
  description:
    "Spec 03 §2.1 and §5 row 3. Gated by `planning.read`. `status` is `active` by default, `archived` or `all`. Each row embeds the product's active safety-stock policy at the facility, null when none. Cursor keyset, `limit` 1 to 200, default 50. " +
    FACILITY_DOOR,
  request: { query: PlanningParametersListQuery },
  responses: {
    200: json("One page of parameter rows", PlanningParametersListResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/planning/parameters/{productId}",
  operationId: "putPlanningParameters",
  summary: "Create, update, archive or restore a product's planning parameters",
  description:
    "Spec 03 §2.1 and §5 row 4. Gated by `planning.demand.manage`. The body is the full row with `status` and `expected_updated_at`, null for a product with no row; 201 when the row is created, 200 otherwise. Archiving archives the product's active safety-stock policy at the facility too. Refusals in order: 403, then " +
    FACILITY_DOOR +
    " Then 404 `PRODUCT_NOT_FOUND`, 400 for a field out of its CHECK, 404 `PLANNING_PARAMETERS_NOT_FOUND` for `status: archived` on a product with no row, 422 `PRODUCT_ARCHIVED` for `status: active` on a product module 01 archived, 422 `PLANNING_PARAMETERS_INVALID` with `reasons`, and 409 `PLANNING_PARAMETERS_CHANGED` with the stored row as `current`. Appends `planning_parameters.upserted` or `planning_parameters.archived`.",
  request: {
    params: PlanningProductParam,
    query: PlanningFacilityQuery,
    body: { content: { "application/json": { schema: PlanningParametersPutRequest } } },
  },
  responses: {
    200: json("The updated row", PlanningParameters),
    201: json("The created row", PlanningParameters),
    400: problemResponse(
      "VALIDATION_FAILED: no `facility_id` or a field out of its CHECK / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / PRODUCT_NOT_FOUND / PLANNING_PARAMETERS_NOT_FOUND"),
    409: problemOf(
      "PLANNING_PARAMETERS_CHANGED: `expected_updated_at` is stale; `current` holds the stored row",
      PlanningParametersConflictProblem,
    ),
    422: problemOf(
      "PRODUCT_ARCHIVED / PLANNING_PARAMETERS_INVALID with `reasons`: METHOD_FIELDS_MISMATCH, TRANSFER_SOURCE_REQUIRED, SOURCE_NOT_ALLOWED, SOURCE_FACILITY_INVALID",
      PlanningParametersInvalidProblem,
    ),
  },
});

// --- safety stock (rows 5 and 6) ------------------------------------------------------

route({
  method: "get",
  path: "/v1/planning/safety-stock-policies",
  operationId: "listSafetyStockPolicies",
  summary: "List the safety-stock policies of a facility",
  description:
    "Spec 03 §2.2 and §5 row 5. Gated by `planning.read`. `status` is `active` by default, `archived` or `all`. Cursor keyset, `limit` 1 to 200, default 50. " +
    FACILITY_DOOR,
  request: { query: SafetyStockPolicyListQuery },
  responses: {
    200: json("One page of policies", SafetyStockPolicyListResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/planning/safety-stock-policies/{productId}",
  operationId: "putSafetyStockPolicy",
  summary: "Create, update or archive a product's active safety-stock policy",
  description:
    "Spec 03 §2.2 and §5 row 6. Gated by `planning.demand.manage`. Updates the active policy in place; `status: archived` archives it, and a later `status: active` inserts a new one (201 when a row is created, 200 otherwise). `valid_from` defaults to the facility's business today. `expected_updated_at` is the active policy's, null when none is active. Refusals in order: 403, then " +
    FACILITY_DOOR +
    " Then 404 `PRODUCT_NOT_FOUND`, 400 for a field out of its CHECK, 404 `SAFETY_STOCK_NOT_FOUND` for `status: archived` with no active policy, 422 `PRODUCT_ARCHIVED` for `status: active` on a product module 01 archived, 422 `SAFETY_STOCK_INVALID` with `reasons`, and 409 `SAFETY_STOCK_CHANGED` with the active policy as `current`. Appends `safety_stock_policy.upserted` or `safety_stock_policy.archived`.",
  request: {
    params: PlanningProductParam,
    query: PlanningFacilityQuery,
    body: { content: { "application/json": { schema: SafetyStockPolicyPutRequest } } },
  },
  responses: {
    200: json("The updated or archived policy", SafetyStockPolicy),
    201: json("The created policy", SafetyStockPolicy),
    400: problemResponse(
      "VALIDATION_FAILED: no `facility_id`, a negative `static_qty`, a `days_of_cover` below 1 or a malformed date / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / PRODUCT_NOT_FOUND / SAFETY_STOCK_NOT_FOUND"),
    409: problemOf(
      "SAFETY_STOCK_CHANGED: `expected_updated_at` is stale; `current` holds the active policy or null",
      SafetyStockConflictProblem,
    ),
    422: problemOf(
      "PRODUCT_ARCHIVED / SAFETY_STOCK_INVALID with `reasons`: METHOD_FIELDS_MISMATCH, VALID_RANGE_INVALID, NO_PLANNING_PARAMETERS",
      SafetyStockInvalidProblem,
    ),
  },
});

// --- forecasts (rows 7 to 11) ------------------------------------------------------

route({
  method: "get",
  path: "/v1/forecasts",
  operationId: "listForecasts",
  summary: "List the forecast versions of a facility",
  description:
    "Spec 03 §2.3 and §5 row 7. Gated by `planning.read`. Cursor keyset, `limit` 1 to 200, default 50. " +
    FACILITY_DOOR,
  request: { query: ForecastListQuery },
  responses: {
    200: json("One page of forecasts", ForecastListResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/forecasts",
  operationId: "createForecast",
  summary: "Create a forecast",
  description:
    "Spec 03 §2.3 and §5 row 8. Gated by `planning.demand.manage`. A new forecast is a `draft`, `consume` with a 14-day window unless the body says otherwise. Refusals in order: 403, 400 (no `facility_id`, a name over 120 characters after trimming, a window outside 0 to 91), 403 for a facility outside scope, 404 `FACILITY_NOT_FOUND`, 409 `FORECAST_NAME_TAKEN` (names compare ignoring case per facility). Appends `forecast.created`.",
  request: {
    body: { content: { "application/json": { schema: ForecastCreateRequest } } },
  },
  responses: {
    201: json("The created forecast", Forecast),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
    409: problemResponse("FORECAST_NAME_TAKEN"),
  },
});

route({
  method: "patch",
  path: "/v1/forecasts/{id}",
  operationId: "updateForecast",
  summary: "Rename a forecast, change its mode or window, activate it or archive it",
  description:
    "Spec 03 §2.3 and §5 row 9. Gated by `planning.demand.manage`. Status moves `draft` to `active`, and `draft` or `active` to `archived`; nothing leaves `archived`. Refusals in order: 403, 404 `FORECAST_NOT_FOUND`, 400 (a window outside 0 to 91, a status other than `active` or `archived`, a name over 120 characters, no `expected_updated_at`), 409 `FORECAST_CHANGED` with the stored header as `current`, 409 `FORECAST_ARCHIVED`, 409 `FORECAST_STATUS_TRANSITION_INVALID`, 409 `FORECAST_NAME_TAKEN`. Appends `forecast.updated`, `forecast.activated` or `forecast.archived`.",
  request: {
    params: ForecastIdParam,
    body: { content: { "application/json": { schema: ForecastPatchRequest } } },
  },
  responses: {
    200: json("The updated forecast", Forecast),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FORECAST_NOT_FOUND"),
    409: problemOf(
      "FORECAST_CHANGED with `current` / FORECAST_ARCHIVED / FORECAST_STATUS_TRANSITION_INVALID / FORECAST_NAME_TAKEN",
      ForecastConflictProblem,
    ),
  },
});

route({
  method: "get",
  path: "/v1/forecasts/{id}",
  operationId: "getForecast",
  summary: "Read a forecast and the lines of one window",
  description:
    "Spec 03 §2.4 and §5 row 10. Gated by `planning.read`. `bucket_from` and `bucket_to` are inclusive Mondays; by default the window runs from the current week's Monday for the facility's `horizon_weeks`. The answer carries the window's lines with ids and notes and the count of lines outside it. Refusals in order: 403, 404 `FORECAST_NOT_FOUND`, 400 (a bound that is not a Monday, `bucket_to` before `bucket_from`, or 400 `FORECAST_WINDOW_TOO_WIDE` beyond 104 weeks).",
  request: { params: ForecastIdParam, query: ForecastDetailQuery },
  responses: {
    200: json("The forecast and its window", ForecastDetail),
    400: problemResponse("VALIDATION_FAILED / FORECAST_WINDOW_TOO_WIDE"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FORECAST_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/forecasts/{id}/lines",
  operationId: "replaceForecastLines",
  summary: "Replace the lines of one forecast window",
  description:
    "Spec 03 §2.4 and §5 row 11 (D-349 ruling 16). Gated by `planning.demand.manage`. Lines upsert on product and bucket and keep their ids. A line of the window the payload omits is deleted, or set to 0 with its note cleared while a retained pool entry references it; with `product_ids`, only those products' lines are touched. A line outside the window is never touched. The answer carries the header's new `updated_at`. Refusals in order: 403, 404 `FORECAST_NOT_FOUND`, 400 (no `expected_updated_at`, a bound that is not a Monday, `bucket_from` before the current week's Monday, `FORECAST_WINDOW_TOO_WIDE` beyond 104 weeks, `FORECAST_LINES_TOO_MANY` beyond 2 000 rows), 409 `FORECAST_CHANGED` with the stored header as `current`, 409 `FORECAST_ARCHIVED`, 422 `FORECAST_LINES_INVALID` with each failing row in `rows` and nothing written. Appends `forecast.lines_replaced`.",
  request: {
    params: ForecastIdParam,
    body: { content: { "application/json": { schema: ForecastLinesPutRequest } } },
  },
  responses: {
    200: json("The header and the saved window", ForecastDetail),
    400: problemResponse(
      "VALIDATION_FAILED / FORECAST_WINDOW_TOO_WIDE / FORECAST_LINES_TOO_MANY / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FORECAST_NOT_FOUND"),
    409: problemOf("FORECAST_CHANGED with `current` / FORECAST_ARCHIVED", ForecastConflictProblem),
    422: problemOf(
      "FORECAST_LINES_INVALID: `rows` lists each failing row's index and codes",
      ForecastLinesInvalidProblem,
    ),
  },
});

// --- the demand pool (rows 12 to 16, G-2) -------------------------------------------

const REFRESH_READ =
  "A `refresh_id` that names no refresh at the facility answers 404 `DEMAND_REFRESH_NOT_FOUND`, one that is running or failed 409 `DEMAND_REFRESH_NOT_SUCCEEDED`, and one whose entries §2.6's retention purged 409 `DEMAND_REFRESH_PURGED`.";

route({
  method: "get",
  path: "/v1/forecasts/{id}/consumption",
  operationId: "getForecastConsumption",
  summary: "Read which order lines consumed a forecast in one refresh",
  description:
    "Spec 03 §2.6b and §5 row 12 (D-349 ruling 6). Gated by `planning.read`. Per forecast cell of the refresh, the consuming order lines from `demand_consumptions`, each with its label rendered for the reader (D-349 ruling 15). Without `refresh_id` the latest succeeded refresh at the forecast's facility is read, and a facility with none answers `demand_refresh_id: null` with no cells. Refusals in order: 403, 404 `FORECAST_NOT_FOUND`, 404 `DEMAND_REFRESH_NOT_FOUND` (also for a refresh of another facility), 409 `DEMAND_REFRESH_NOT_SUCCEEDED`, 409 `DEMAND_REFRESH_PURGED`.",
  request: { params: ForecastIdParam, query: ForecastConsumptionQuery },
  responses: {
    200: json("The forecast's consumption in the refresh", ForecastConsumption),
    400: problemResponse("VALIDATION_FAILED: a malformed `refresh_id`"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FORECAST_NOT_FOUND / DEMAND_REFRESH_NOT_FOUND"),
    409: problemResponse("DEMAND_REFRESH_NOT_SUCCEEDED / DEMAND_REFRESH_PURGED"),
  },
});

route({
  method: "post",
  path: "/v1/demand/refresh",
  operationId: "requestDemandRefresh",
  summary: "Refresh a facility's demand pool now",
  description:
    "Spec 03 §3.1 and §5 row 13 (D-349 ruling 9). Gated by `planning.demand.manage`. The claim runs in the request and the assembly runs after the answer, so the caller polls `GET /v1/demand/refreshes/{id}`. A refresh already running at the facility is answered with its own id. A refresh that meets an unbound sales registry is answered 202 and then reads `failed` with `SALES_DEMAND_UNBOUND`. Refusals in order: 403, 400 (no `facility_id`, `horizon_weeks` outside 1 to 52), 403 for a facility outside scope, 404 `FACILITY_NOT_FOUND`. A new run appends `demand_refresh.requested`.",
  request: { body: { content: { "application/json": { schema: DemandRefreshRequest } } } },
  responses: {
    202: json("The claimed refresh, or the one already running", DemandRefreshAccepted),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/demand/refreshes",
  operationId: "listDemandRefreshes",
  summary: "List a facility's demand refreshes",
  description:
    "Spec 03 §5 row 14. Gated by `planning.read`. Newest first, cursor keyset, `limit` 1 to 200, default 50. The answer carries the facility's pending debounce request and its consecutive failures since the last success. " +
    FACILITY_DOOR,
  request: { query: DemandRefreshListQuery },
  responses: {
    200: json("One page of refreshes", DemandRefreshListResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/demand/refreshes/{id}",
  operationId: "getDemandRefresh",
  summary: "Read one demand refresh",
  description:
    "Spec 03 §5 row 15. Gated by `planning.read`. The row with the labels of the order lines its stats list as skipped or credit deferred, rendered for the reader. Refusals in order: 403, 404 `DEMAND_REFRESH_NOT_FOUND` (also for a refresh outside the caller's facility scope).",
  request: { params: DemandRefreshIdParam },
  responses: {
    200: json("The refresh", DemandRefreshDetail),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("DEMAND_REFRESH_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/demand/entries",
  operationId: "listDemandEntries",
  summary: "Read a facility's demand pool",
  description:
    "Spec 03 §2.6 and §5 row 16. Gated by `planning.read`. The entries of one succeeded refresh, by default the facility's latest, with per product and bucket subtotals over the whole filtered set. Quantities are in the product's base unit. A sales-order entry carries its label for the reader (D-349 ruling 15). Before the facility's first successful refresh the answer is `refresh: null` with no entries. " +
    FACILITY_DOOR +
    " " +
    REFRESH_READ,
  request: { query: DemandEntriesQuery },
  responses: {
    200: json("One page of the pool", DemandEntriesResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / DEMAND_REFRESH_NOT_FOUND"),
    409: problemResponse("DEMAND_REFRESH_NOT_SUCCEEDED / DEMAND_REFRESH_PURGED"),
  },
});

// --- the master production schedule (rows 18 to 24, G-3) ---------------------------

const MPS_LOCKS =
  "Every MPS mutation takes the facility's `'mps'` advisory lock, then the plan row, then any line row, in that order (D-350 ruling 10).";

route({
  method: "get",
  path: "/v1/mps/plans",
  operationId: "listMpsPlans",
  summary: "List a facility's MPS plans",
  description:
    "Spec 03 §2.7 and §5 row 18. Gated by `planning.read`. Newest first by `proposed_at`, cursor keyset, `limit` 1 to 200, default 50; superseded plans included. Each row carries `demand_changed_since_proposal`, read for the page in one statement, so the workbench tile can say demand moved without reading the plan. " +
    FACILITY_DOOR,
  request: { query: MpsPlanListQuery },
  responses: {
    200: json("One page of plans", MpsPlanListResponse),
    400: problemResponse("VALIDATION_FAILED: `facility_id` missing, or a malformed filter"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/mps/plans/{id}",
  operationId: "getMpsPlan",
  summary: "Read an MPS plan and one page of its cells",
  description:
    "Spec 03 §2.7, §2.8 and §5 row 19 (D-350 rulings 4, 6 and 7). Gated by `planning.read`. The header with `delta_pct` and `delta_by_uom` from their stored columns, `effective_totals` for the whole plan, `demand_changed_cells` while `pending_approval` and `demand_changed_since_proposal`, then the cells of up to 50 products ordered by product id, each with `effective_qty` and `projected_on_hand_effective` recomputed from the effective quantities. `next_cursor` names the next product page. `transfer_destinations` names the other active facilities whose active `transfer` parameters source one of the plan's products from its facility, read across the tenant whatever the caller's scope (D-349 ruling 4). Refusals in order: 403, 404 `MPS_PLAN_NOT_FOUND` (also for a plan outside the caller's facility scope), 400 `VALIDATION_FAILED` (a malformed cursor).",
  request: { params: MpsPlanIdParam, query: MpsPlanDetailQuery },
  responses: {
    200: json("The plan and one product page of its cells", MpsPlanDetail),
    400: problemResponse("VALIDATION_FAILED: a malformed cursor"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("MPS_PLAN_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/mps/plans/propose",
  operationId: "proposeMpsPlan",
  summary: "Propose a facility's MPS now",
  description:
    "Spec 03 §3.2 and §5 row 20 (D-350 ruling 9). Gated by `planning.mps.edit`. Always proposes from the facility's latest successful refresh, re-reading the stock position and the open supply, supersedes a `proposed` or `adjusting` plan and carries its unchanged overrides, and answers 201 with the new plan. With only an approved plan in force the new plan opens beside it. Refusals in order: 403, then " +
    FACILITY_DOOR +
    " Then 409 `MPS_NO_RELEVANT_PRODUCTS` (no active MPS-relevant parameter at the facility), 409 `MPS_PLAN_PENDING_APPROVAL` (a submitted plan waits on an approver), 409 `DEMAND_REFRESH_MISSING` (no successful refresh, or the latest is of an earlier week than the facility's current one). " +
    MPS_LOCKS +
    " Appends `mps_plan.proposed_manually` and emits `planning.mps_proposed`.",
  request: { body: { content: { "application/json": { schema: MpsProposeRequest } } } },
  responses: {
    201: json("The new plan", MpsPlan),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
    409: problemOf(
      "MPS_NO_RELEVANT_PRODUCTS / MPS_PLAN_PENDING_APPROVAL with `mps_plan_id` / DEMAND_REFRESH_MISSING",
      MpsProposeConflictProblem,
    ),
  },
});

route({
  method: "post",
  path: "/v1/mps/plans/{id}/lines/{lineId}/override",
  operationId: "overrideMpsLine",
  summary: "Override one cell of an open MPS plan",
  description:
    "Spec 03 §2.8 and §5 row 21 (D-350 ruling 9). Gated by `planning.mps.edit`. `override_qty: null`, or a value equal to `proposed_qty`, clears the override. A value that differs needs `override_reason`. `expected_updated_at` is the line's `updated_at`. An accepted override, a clear included, moves a `proposed` plan to `adjusting`. Refusals in order: 403, 404 `MPS_PLAN_NOT_FOUND`, 404 `MPS_LINE_NOT_FOUND` (a line of another plan), 400 (no `expected_updated_at`, a negative quantity, more than three decimals, a reason over 500 characters), 409 `MPS_PLAN_NOT_EDITABLE` (the plan is not `proposed` or `adjusting`), 409 `MPS_LINE_FROZEN`, 422 `MPS_OVERRIDE_REASON_REQUIRED`, 409 `MPS_LINE_CHANGED` with the line as `current`. " +
    MPS_LOCKS +
    " Appends `mps_plan.overridden`.",
  request: {
    params: MpsLineParams,
    body: { content: { "application/json": { schema: MpsOverrideRequest } } },
  },
  responses: {
    200: json("The plan header and the cell as saved", MpsOverrideResponse),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("MPS_PLAN_NOT_FOUND / MPS_LINE_NOT_FOUND"),
    409: problemOf(
      "MPS_PLAN_NOT_EDITABLE with `plan_status` / MPS_LINE_FROZEN / MPS_LINE_CHANGED with `current`",
      MpsLineConflictProblem,
    ),
    422: problemResponse("MPS_OVERRIDE_REASON_REQUIRED"),
  },
});

route({
  method: "post",
  path: "/v1/mps/plans/{id}/submit",
  operationId: "submitMpsPlan",
  summary: "Submit an MPS plan for approval",
  description:
    "Spec 03 §3.2 and §5 row 22 (D-350 ruling 7). Gated by `planning.mps.edit`. Computes and stores `delta_pct` and `delta_by_uom` against `previous_approved_plan_id` under the lock. Below the facility's `mps_auto_approve_delta_pct` the plan approves itself at one instant, recorded as approved by the submitter with `auto_approved: true`, supersedes the previous approved plan, appends `mps_plan.approved` and emits `planning.mps_approved`; otherwise it reads `pending_approval` and appends `mps_plan.submitted`. Refusals in order: 403, 404 `MPS_PLAN_NOT_FOUND`, 400 (a body that is not empty), 409 `MPS_PLAN_NOT_EDITABLE` with `plan_status`. " +
    MPS_LOCKS,
  request: {
    params: MpsPlanIdParam,
    body: { content: { "application/json": { schema: MpsSubmitRequest } } },
  },
  responses: {
    200: json("The plan, `pending_approval` or `approved`", MpsPlan),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("MPS_PLAN_NOT_FOUND"),
    409: problemOf("MPS_PLAN_NOT_EDITABLE with `plan_status`", MpsPlanStatusProblem),
  },
});

route({
  method: "post",
  path: "/v1/mps/plans/{id}/approve",
  operationId: "approveMpsPlan",
  summary: "Approve a submitted MPS plan",
  description:
    "Spec 03 §3.2 and §5 row 23 (D-231, D-350 ruling 7, D-351 ruling 5). Gated by `planning.mps.approve`. Routed by the lineage root of the caller's grants that carry the code and reach the plan's facility: group_admin approves any delta, facility_admin up to `mps_facility_admin_delta_pct` as it reads now, an unrecognised clone nothing. Supersedes the previous approved plan, appends `mps_plan.approved` and emits `planning.mps_approved`. Refusals in order: 403, 404 `MPS_PLAN_NOT_FOUND`, 400 (an empty note, or one over 500 characters), 409 `MPS_PLAN_NOT_PENDING` with `plan_status`, 403 `APPROVAL_THRESHOLD_EXCEEDED` with `delta_pct` and `required_role_lineage: group_admin`. " +
    MPS_LOCKS,
  request: {
    params: MpsPlanIdParam,
    body: { content: { "application/json": { schema: MpsApproveRequest } } },
  },
  responses: {
    200: json("The approved plan", MpsPlan),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemOf(
      "PERMISSION_DENIED, or APPROVAL_THRESHOLD_EXCEEDED with `delta_pct` and `required_role_lineage`",
      MpsApproveForbiddenProblem,
    ),
    404: problemResponse("MPS_PLAN_NOT_FOUND"),
    409: problemOf("MPS_PLAN_NOT_PENDING with `plan_status`", MpsPlanStatusProblem),
  },
});

route({
  method: "post",
  path: "/v1/mps/plans/{id}/reject",
  operationId: "rejectMpsPlan",
  summary: "Return a submitted MPS plan to the planner",
  description:
    "Spec 03 §3.2 and §5 row 24 (D-350 ruling 11). Gated by `planning.mps.approve`. Moves the plan to `adjusting`, writes the rejection columns with the required note and clears the delta and the submit. Refusals in order: 403, 404 `MPS_PLAN_NOT_FOUND`, 400 (no note, or one over 500 characters), 409 `MPS_PLAN_NOT_PENDING` with `plan_status`. " +
    MPS_LOCKS +
    " Appends `mps_plan.rejected`.",
  request: {
    params: MpsPlanIdParam,
    body: { content: { "application/json": { schema: MpsRejectRequest } } },
  },
  responses: {
    200: json("The returned plan", MpsPlan),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("MPS_PLAN_NOT_FOUND"),
    409: problemOf("MPS_PLAN_NOT_PENDING with `plan_status`", MpsPlanStatusProblem),
  },
});

// --- calendars (rows 26 to 36, P3-H-1) ------------------------------------------

const CALENDAR_READ =
  "Gated by `planning.calendar.read`, held by group_admin, facility_admin, planner and maintenance (D-390 ruling 1).";
const CALENDAR_MANAGE =
  "Gated by `planning.calendar.manage`, held by group_admin and facility_admin (D-390 ruling 1).";

route({
  method: "get",
  path: "/v1/shift-patterns",
  operationId: "listShiftPatterns",
  summary: "List the tenant's shift patterns",
  description:
    "Spec 03 §2.12 and §5 row 26. " +
    CALENDAR_READ +
    " Patterns are tenant-wide. `status` narrows to `active` or `archived`; omitted lists both. Ordered by id, cursor keyset, `limit` 1 to 200, default 50. Refusals in order: 403, 400 (a `status` other than `active` or `archived`, a malformed cursor).",
  request: { query: ShiftPatternListQuery },
  responses: {
    200: json("One page of patterns", ShiftPatternListResponse),
    400: problemResponse("VALIDATION_FAILED: an unknown `status` or a malformed cursor"),
    403: problemResponse("PERMISSION_DENIED"),
  },
});

route({
  method: "post",
  path: "/v1/shift-patterns",
  operationId: "createShiftPattern",
  summary: "Create a shift pattern",
  description:
    "Spec 03 §2.12 and §5 row 27 (D-386 rulings 7 and 9). " +
    CALENDAR_MANAGE +
    " Any holder may create a pattern. Refusals in order: 403, 400 (the body's shape: a code that is not 1 to 64 of `A-Z`, `0-9`, `_` and `-` starting with a letter or digit, a name of 0 or more than 120 characters, more than 42 segments, an unknown field), 422 `SHIFT_PATTERN_INVALID` with `issues` naming each failing segment (§3.3a: weekday, wall times, shift code, efficiency 10 to 200 as a whole number, zero length, overlap on the week), 409 `SHIFT_PATTERN_CODE_TAKEN`. Appends `shift_pattern.created`.",
  request: { body: { content: { "application/json": { schema: ShiftPatternCreateRequest } } } },
  responses: {
    201: json("The created pattern", ShiftPattern),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    409: problemOf("SHIFT_PATTERN_CODE_TAKEN", ShiftPatternConflictProblem),
    422: problemOf("SHIFT_PATTERN_INVALID with `issues`", ShiftPatternInvalidProblem),
  },
});

route({
  method: "patch",
  path: "/v1/shift-patterns/{id}",
  operationId: "patchShiftPattern",
  summary: "Rename, edit the segments of, archive or restore a shift pattern",
  description:
    "Spec 03 §2.12 and §5 row 28 (D-386 ruling 9). " +
    CALENDAR_MANAGE +
    " `expected_updated_at` is required. A body that changes nothing writes nothing. Refusals in order: 403, 404 `SHIFT_PATTERN_NOT_FOUND`, 400 (the body), 409 `SHIFT_PATTERN_CHANGED` with the stored row as `current`, 403 `SHIFT_PATTERN_OUT_OF_SCOPE` naming in `facilities` each facility outside the caller's scope whose assignment or facility calendar uses the pattern, when the body changes `segments` or `status` (a group-scoped holder is never fenced), 422 `SHIFT_PATTERN_INVALID` with `issues`, 409 `SHIFT_PATTERN_IN_USE` naming `assignment_ids` and `facility_ids` when archiving a pattern an open assignment (one with no end, or an end on or after its facility's today) or a facility calendar uses. Appends `shift_pattern.archived` when it archives, `shift_pattern.updated` otherwise.",
  request: {
    params: ShiftPatternIdParam,
    body: { content: { "application/json": { schema: ShiftPatternPatchRequest } } },
  },
  responses: {
    200: json("The pattern as saved", ShiftPattern),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemOf(
      "PERMISSION_DENIED, or SHIFT_PATTERN_OUT_OF_SCOPE with `facilities`",
      ShiftPatternForbiddenProblem,
    ),
    404: problemResponse("SHIFT_PATTERN_NOT_FOUND"),
    409: problemOf(
      "SHIFT_PATTERN_CHANGED with `current`, or SHIFT_PATTERN_IN_USE with `assignment_ids` and `facility_ids`",
      ShiftPatternConflictProblem,
    ),
    422: problemOf("SHIFT_PATTERN_INVALID with `issues`", ShiftPatternInvalidProblem),
  },
});

route({
  method: "get",
  path: "/v1/work-center-calendars",
  operationId: "listWorkCenterCalendars",
  summary: "List the pattern assignments of a facility's work centres",
  description:
    "Spec 03 §2.13 and §5 row 29 (D-386 ruling 8). " +
    CALENDAR_READ +
    " Each row carries `missing_shift_codes`, the pattern's shift codes with no active shift definition in module 04 at the facility, a warning that never blocks a save. Ordered by id, cursor keyset. Refusals in order: 403, then " +
    FACILITY_DOOR +
    " Then 404 `WORK_CENTER_NOT_FOUND` for a `work_center_id` that is not at that facility.",
  request: { query: WorkCenterCalendarListQuery },
  responses: {
    200: json("One page of assignments", WorkCenterCalendarListResponse),
    400: problemResponse("VALIDATION_FAILED: no `facility_id`, or a malformed filter or cursor"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/work-center-calendars",
  operationId: "createWorkCenterCalendar",
  summary: "Assign a shift pattern to a work centre",
  description:
    "Spec 03 §2.13 and §5 row 30. " +
    CALENDAR_MANAGE +
    " `valid_to` omitted or null is open-ended; both bounds are inclusive facility dates. Refusals in order: 403, 400 (the body, `valid_to` before `valid_from`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` (also an archived work centre or one at another facility), 404 `SHIFT_PATTERN_NOT_FOUND` (also an archived pattern), 409 `OVERLAPPING_ASSIGNMENT` naming the assignment it meets in `overlapping`. Appends `work_center_calendar.assigned`.",
  request: {
    body: { content: { "application/json": { schema: WorkCenterCalendarCreateRequest } } },
  },
  responses: {
    201: json("The created assignment", WorkCenterCalendar),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND / SHIFT_PATTERN_NOT_FOUND"),
    409: problemOf("OVERLAPPING_ASSIGNMENT with `overlapping`", WorkCenterCalendarConflictProblem),
  },
});

route({
  method: "patch",
  path: "/v1/work-center-calendars/{id}",
  operationId: "patchWorkCenterCalendar",
  summary: "End (or reopen) a pattern assignment",
  description:
    "Spec 03 §2.13 and §5 row 31. " +
    CALENDAR_MANAGE +
    " The body is `valid_to`, the last date covered; null makes the assignment open-ended again. A body that changes nothing writes nothing. Refusals in order: 403, 404 `WORK_CENTER_CALENDAR_NOT_FOUND` (also an assignment outside the caller's scope), 400 (`valid_to` before `valid_from`), 409 `OVERLAPPING_ASSIGNMENT`. Appends `work_center_calendar.ended`.",
  request: {
    params: WorkCenterCalendarIdParam,
    body: { content: { "application/json": { schema: WorkCenterCalendarPatchRequest } } },
  },
  responses: {
    200: json("The assignment as saved", WorkCenterCalendar),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("WORK_CENTER_CALENDAR_NOT_FOUND"),
    409: problemOf("OVERLAPPING_ASSIGNMENT with `overlapping`", WorkCenterCalendarConflictProblem),
  },
});

route({
  method: "delete",
  path: "/v1/work-center-calendars/{id}",
  operationId: "deleteWorkCenterCalendar",
  summary: "Remove an assignment that has not started",
  description:
    "Spec 03 §2.13 and §5 row 32. " +
    CALENDAR_MANAGE +
    " Refusals in order: 403, 404 `WORK_CENTER_CALENDAR_NOT_FOUND` (also an assignment outside the caller's scope), 409 `ASSIGNMENT_IN_EFFECT` once `valid_from` is on or before the facility's today (end it with PATCH instead). Appends `work_center_calendar.removed`.",
  request: { params: WorkCenterCalendarIdParam },
  responses: {
    204: { description: "Removed" },
    400: problemResponse("IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("WORK_CENTER_CALENDAR_NOT_FOUND"),
    409: problemResponse("ASSIGNMENT_IN_EFFECT"),
  },
});

route({
  method: "get",
  path: "/v1/calendar-exceptions",
  operationId: "listCalendarExceptions",
  summary: "List a facility's calendar exceptions in a date window",
  description:
    "Spec 03 §2.14 and §5 row 33. " +
    CALENDAR_READ +
    " `from` and `to` are inclusive facility business dates at most 731 days apart; a row is listed when its interval meets the window as the facility's clock reads it. `work_center_id` narrows to that work centre's own rows; facility-wide rows have none. Ordered by id, cursor keyset. Refusals in order: 403, 400 (no `facility_id`, `from` or `to` missing or reversed, a window over 731 days, an unknown `kind`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` (not at that facility).",
  request: { query: CalendarExceptionListQuery },
  responses: {
    200: json("One page of exceptions", CalendarExceptionListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/calendar-exceptions",
  operationId: "createCalendarException",
  summary: "Record a calendar exception",
  description:
    "Spec 03 §2.14 and §5 row 34 (D-386 rulings 6 and 7). " +
    CALENDAR_MANAGE +
    " Instants carry an explicit zone. `work_center_id` omitted or null makes the exception facility-wide. Refusals in order: 403, 400 (the body's shape: an unknown `kind`, an instant without a zone, a `capacity_factor` that is not a decimal with at most two places, a note over 500 characters), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` (also an archived work centre or one at another facility), 422 `CALENDAR_EXCEPTION_INVALID` with `reasons` (`ends_not_after_starts`, `factor_out_of_range` above 3, `factor_above_one_for_kind` outside `overtime` and `extra_shift`). Appends `calendar_exception.created`.",
  request: {
    body: { content: { "application/json": { schema: CalendarExceptionCreateRequest } } },
  },
  responses: {
    201: json("The created exception", CalendarException),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
    422: problemOf("CALENDAR_EXCEPTION_INVALID with `reasons`", CalendarExceptionInvalidProblem),
  },
});

route({
  method: "delete",
  path: "/v1/calendar-exceptions/{id}",
  operationId: "deleteCalendarException",
  summary: "Remove a manual calendar exception",
  description:
    "Spec 03 §2.14 and §5 row 35. " +
    CALENDAR_MANAGE +
    " Refusals in order: 403, 404 `CALENDAR_EXCEPTION_NOT_FOUND` (also an exception outside the caller's scope). Module 08's migration adds 403 `CALENDAR_EXCEPTION_READ_ONLY` for its sync rows with their writer. Appends `calendar_exception.removed`.",
  request: { params: CalendarExceptionIdParam },
  responses: {
    204: { description: "Removed" },
    400: problemResponse("IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("CALENDAR_EXCEPTION_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/facility-calendar/preview",
  operationId: "previewFacilityCalendar",
  summary: "Resolve a facility's (or a work centre's) working days and shifts",
  description:
    "Spec 03 §3.3a, §4.8 and §5 row 36 (D-386 ruling 3). " +
    CALENDAR_READ +
    " Serves `facility_pattern`, the facility calendar's pattern from `planning_settings`, so a reader without `planning.read` sees it, then each date from `from` for `days` dates (1 to 62, default 28): whether it is calendared and working, its segments resolved in the facility's zone with their effective minutes, and the exceptions that close it. With `work_center_id` the dates resolve on that work centre's assignments and exceptions. Refusals in order: 403, 400 (no `facility_id` or `from`, `days` outside 1 to 62), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` (not at that facility).",
  request: { query: FacilityCalendarPreviewQuery },
  responses: {
    200: json("The resolved dates", FacilityCalendarPreview),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

// --- MRP runs and planned orders (rows 38 to 49, P3-H-2) ---------------------------

const ORDER_READ =
  "Gated by `planning.planned_order.read`, held by group_admin, facility_admin, planner, procurement and finance (D-390 ruling 1). A reader whose role lineage holds procurement and none of group_admin, facility_admin and planner is served purchase orders only, at every read (spec 03 §7 narrowing (c), D-387 ruling 5).";
const ORDER_MANAGE =
  "Gated by `planning.planned_order.manage`, held by group_admin, facility_admin and planner (D-390 ruling 1).";
const SET_SEQ =
  "The write takes the order's row `FOR UPDATE` and then the `planning_settings` row of its facility, whose `planned_order_set_seq` it raises by 1, so a run computing at that moment is requeued at its write (spec 03 §2.9's lock order, D-388 ruling 2).";

route({
  method: "post",
  path: "/v1/mrp/runs",
  operationId: "requestMrpRun",
  summary: "Request an MRP run at a facility",
  description:
    "Spec 03 §3.3 and §5 row 38 (D-388 rulings 1 and 10). Gated by `planning.mrp.run`, held by group_admin, facility_admin and planner. A request while a run is queued joins it: `trigger_count` rises, `due_at` becomes the earlier of the two, and the trigger is replaced only by a higher one (`mps_approval` > `manual` > `transfer_projection` > `cron`). A request while one runs and none is queued inserts the queued row. No plan is named: a run reads the facility's latest approved plan when it is claimed. A facility with no calendar answers 202, and its run then fails `FACILITY_CALENDAR_MISSING`. Appends `mrp_run.requested`. Refusals in order: 403, 400 (no `facility_id`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { body: { content: { "application/json": { schema: MrpRunRequest } } } },
  responses: {
    202: json("The facility's queued run, joined or inserted", MrpRunAccepted),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/mrp/runs/group-wide",
  operationId: "requestMrpGroupPass",
  summary: "Run the orchestrated group-wide MRP pass now",
  description:
    "Spec 03 §3.3's manual pass and §5 row 39 (D-388 rulings 5 and 10). Gated by `planning.mrp.run` held at group scope. Covers every active facility of the tenant, destination before source, a cyclic group by the count of products each sources inside it, ties by code; each facility is queued with trigger `manual` only when the pass reaches it, and no `nightly_mrp_on` is stamped. A facility with no calendar and nothing to plan is skipped and named in `skipped[]`; one with something to plan is run and fails `FACILITY_CALENDAR_MISSING`. Each run is read through `GET /v1/mrp/runs?facility_id=&orchestration_id=`. Appends `mrp_run.requested`. Refusals in order: 403, 403 `MRP_GROUP_SCOPE_REQUIRED` (the code held at facility scope only), 409 `MRP_GROUP_PASS_RUNNING` with the running pass's `orchestration_id`.",
  request: { body: { content: { "application/json": { schema: MrpGroupPassRequest } } } },
  responses: {
    202: json("The pass's facilities in order and its skips", MrpGroupPassAccepted),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED / MRP_GROUP_SCOPE_REQUIRED"),
    409: problemOf("MRP_GROUP_PASS_RUNNING with `orchestration_id`", MrpGroupPassConflictProblem),
  },
});

route({
  method: "get",
  path: "/v1/mrp/runs",
  operationId: "listMrpRuns",
  summary: "A facility's MRP run history",
  description:
    "Spec 03 §5 row 40 (D-388 ruling 8). " +
    ORDER_READ +
    " Newest first, cursor keyset, `limit` 1 to 200, default 50. `consecutive_failures` counts the runs that ended `failed` since the facility's last success, leaving out a run merged away by a conflict or a shutdown (`stats.superseded_by` set). A procurement reader is served the stats §7's table names, with `stats_narrowed: true`. Refusals in order: 403, 400 (no `facility_id`, an unknown `status`, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: MrpRunListQuery },
  responses: {
    200: json("One page of runs", MrpRunListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/mrp/runs/{id}",
  operationId: "getMrpRun",
  summary: "One MRP run with its stats",
  description:
    "Spec 03 §4.5's run detail and §5 row 41. " +
    ORDER_READ +
    " Every stats list holds at most 200 entries beside its exact count. Refusals in order: 403, 404 `MRP_RUN_NOT_FOUND` (also a run outside the caller's scope).",
  request: { params: MrpRunIdParam },
  responses: {
    200: json("The run", MrpRun),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("MRP_RUN_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/planned-orders",
  operationId: "listPlannedOrders",
  summary: "A facility's planned orders",
  description:
    "Spec 03 §4.5 and §5 row 42 (D-387 rulings 7 and 8). " +
    ORDER_READ +
    " `action_message` is `true`, `false` or one message kind; `from` and `to` bound the due date, both inclusive; `pegged_to_sales_order_line_id` and `pegged_to_demand_entry_id` walk down from the need through parents and transfer hops at the reader's facilities. `sort` is `due_date` (the default) or `po_no`, cursor keyset, `limit` 1 to 200, default 50. Each row carries `stale_pin`, computed at read with module 01's pickers. Refusals in order: 403, 400 (no `facility_id`, a filter or sort that does not parse), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: PlannedOrderListQuery },
  responses: {
    200: json("One page of planned orders", PlannedOrderListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/planned-orders/summary",
  operationId: "getPlannedOrderSummary",
  summary: "Planned-order counts for the workbench",
  description:
    "Spec 03 §4.1 and §5 row 43. " +
    ORDER_READ +
    " Counts by status and kind and messages by kind, purchase only for procurement. Refusals in order: 403, 400 (no `facility_id`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: PlanningFacilityQuery },
  responses: {
    200: json("The counts", PlannedOrderSummary),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/planned-orders/{id}",
  operationId: "getPlannedOrder",
  summary: "A planned order with its explanation and pegging",
  description:
    "Spec 03 §2.10, §2.11, §4.5's drawer and §5 row 44 (D-387 rulings 3 and 4). " +
    ORDER_READ +
    " The explanation is served as stored, keys, ids and numbers, with `labels` rendered for the reader: a sales order line through module 09's doors, an order or cell by its number. The walk up and down is bounded at 12 levels and 500 nodes with `walk_hidden` the exact count left out; a hop to a facility outside the reader's scope, or of a kind procurement may not read, is served in the seven-field reduced shape. Refusals in order: 403, 404 `PLANNED_ORDER_NOT_FOUND` (also an order outside the caller's scope or of another kind for procurement).",
  request: { params: PlannedOrderIdParam },
  responses: {
    200: json("The order, its explanation and its walk", PlannedOrderDetail),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PLANNED_ORDER_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/firm",
  operationId: "firmPlannedOrders",
  summary: "Firm planned orders",
  description:
    "Spec 03 §3.3's state machine and §5 row 45 (D-389 rulings 1 and 2). " +
    ORDER_MANAGE +
    " One transaction: the rows are locked in id order, then each facility's settings row in facility-id order, whose `planned_order_set_seq` rises by 1. Answers per id `firmed`, an already firmed order included, or its refusal: 404 `PLANNED_ORDER_NOT_FOUND` (also outside the caller's scope), 409 `PLANNED_ORDER_NOT_PROPOSED` with `order_status`. Each firmed order appends `planned_order.firmed` and emits `planning.planned_order_firmed`. Refusals of the whole request in order: 403, 400 (no ids, more than 200, a malformed id).",
  request: { body: { content: { "application/json": { schema: PlannedOrderFirmRequest } } } },
  responses: {
    200: json("Per id, its outcome", PlannedOrderBulkResponse),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/convert",
  operationId: "convertPlannedOrders",
  summary: "Convert firmed planned orders into their documents",
  description:
    "Spec 03 §3.4 and §5 row 50 (D-389 rulings 3 to 8, D-390 ruling 1). Gated by `planning.planned_order.convert`, held by group_admin, facility_admin, planner and procurement; a procurement lineage converts purchase orders only. " +
    "Each id converts in its own transaction, in id order, and the answer is per id, in the order sent; nothing is all-or-nothing across modules. Each conversion judges the caller's authority over the order first, then takes the order's row `FOR UPDATE`, then its facility's `planning_settings` row, whose `planned_order_set_seq` rises by 1, and only then module 04's or module 06's own path. " +
    "A manufacture order becomes a draft work order on exactly its pinned BOM and routing (an order dated without a routing takes the routing effective at its `start_date`), pegged to a sales order line only when every peg of the order, followed up through transfer hops of its own product, resolves to that one line; an order that is a component of a parent planned order is made to stock. A transfer order becomes a draft transfer with one bulk line, whose lines cannot be edited afterwards. Each writes `converted_kind`, `converted_ref_id`, `converted_by` and `converted_at`, clears the action message, appends `planned_order.converted` and emits `planning.planned_order_converted`. " +
    "Answers `{results: [{id, status, code, converted_kind, converted_ref_id, refusal}]}`, one entry per distinct id in the order sent: `status: converted` for an order converted now or before, `code: ALREADY_CONVERTED` for one converted before, or a `refusal` carrying the problem's `status` and `code`, checked in this order: 404 `PLANNED_ORDER_NOT_FOUND` (also outside the caller's scope, and a manufacture or transfer id from a procurement lineage), `ALREADY_CONVERTED` (success), 409 `PLANNED_ORDER_NOT_FIRMED` with `order_status`, 422 `PURCHASE_CONVERSION_UNAVAILABLE`, 422 `PLANNED_ORDER_PIN_INVALID` for the BOM pin, 422 `PLANNED_ORDER_NO_ROUTING` with `product_id` and `start_date`, 422 `PLANNED_ORDER_PIN_INVALID` for the routing pin (each with the pin and `reason`), then the owning module's codes: module 09's peg codes, 404 `FACILITY_NOT_FOUND`, 404 `PRODUCT_NOT_FOUND` and 409 `TRANSFER_SOURCE_INACTIVE`. " +
    "Refusals of the whole request in order: 403, 400 (no ids, more than 200, a malformed id).",
  request: { body: { content: { "application/json": { schema: PlannedOrderConvertRequest } } } },
  responses: {
    200: json("Per id, its outcome", PlannedOrderConvertResponse),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/{id}/cancel",
  operationId: "cancelPlannedOrder",
  summary: "Cancel a firmed planned order",
  description:
    "Spec 03 §3.3's state machine and §5 row 46 (D-389 ruling 1). " +
    ORDER_MANAGE +
    " Only a firmed order is cancelled by a person; a proposed order is the run's to regenerate, so it answers 409 `PLANNED_ORDER_NOT_FIRMED`. The cancel clears any action message. " +
    SET_SEQ +
    " Appends `planned_order.cancelled`. Refusals in order: 403, 404 `PLANNED_ORDER_NOT_FOUND`, 400 (no reason, one of only spaces, one over 500 characters), 409 `PLANNED_ORDER_NOT_FIRMED` with `order_status`.",
  request: {
    params: PlannedOrderIdParam,
    body: { content: { "application/json": { schema: PlannedOrderCancelRequest } } },
  },
  responses: {
    200: json("The cancelled order", PlannedOrder),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PLANNED_ORDER_NOT_FOUND"),
    409: problemOf("PLANNED_ORDER_NOT_FIRMED with `order_status`", PlannedOrderStatusProblem),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/{id}/action-message/accept",
  operationId: "acceptPlannedOrderActionMessage",
  summary: "Apply a planned order's action message",
  description:
    "Spec 03 §3.8 and §5 row 47 (D-389 ruling 10, D-395 ruling 8). " +
    ORDER_MANAGE +
    " A reschedule sets the due date and `bucket_start` to the suggestion and re-dates start and release on the calendars through the kernel's `redateFirmedOrder`; a quantity message sets the quantity; `cancel` cancels the order `action_message_cancel`; `expedite` sets the release, and the start when needed, to today. The message clears. " +
    SET_SEQ +
    " Appends `planned_order.action_message_accepted`. Refusals in order: 403, 404 `PLANNED_ORDER_NOT_FOUND`, 400 (the body), 409 `PLANNED_ORDER_NOT_FIRMED` (a proposed or cancelled order), 409 `NO_ACTION_MESSAGE`, 409 `ACTION_MESSAGE_CHANGED` with `current_message` when the order's message differs in kind or suggestion from `expected_message`.",
  request: {
    params: PlannedOrderIdParam,
    body: { content: { "application/json": { schema: ActionMessageAcceptRequest } } },
  },
  responses: {
    200: json("The order after the change", PlannedOrder),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PLANNED_ORDER_NOT_FOUND"),
    409: problemOf(
      "PLANNED_ORDER_NOT_FIRMED with `order_status`, NO_ACTION_MESSAGE, or ACTION_MESSAGE_CHANGED with `current_message`",
      ActionMessageConflictProblem,
    ),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/{id}/action-message/dismiss",
  operationId: "dismissPlannedOrderActionMessage",
  summary: "Dismiss a planned order's action message",
  description:
    "Spec 03 §3.8 and §5 row 48 (D-385 ruling 14, D-389 ruling 10). " +
    ORDER_MANAGE +
    " Stores the reason with `dismissed_message_key`, `{kind}|{suggestion}`, and clears the message, which no later run raises again while its suggestion is the same. " +
    SET_SEQ +
    " Appends `planned_order.action_message_dismissed`. Refusals in order: as the accept, with 400 for a reason that is missing, only spaces or over 500 characters.",
  request: {
    params: PlannedOrderIdParam,
    body: { content: { "application/json": { schema: ActionMessageDismissRequest } } },
  },
  responses: {
    200: json("The order with its message dismissed", PlannedOrder),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PLANNED_ORDER_NOT_FOUND"),
    409: problemOf(
      "PLANNED_ORDER_NOT_FIRMED with `order_status`, NO_ACTION_MESSAGE, or ACTION_MESSAGE_CHANGED with `current_message`",
      ActionMessageConflictProblem,
    ),
  },
});

route({
  method: "post",
  path: "/v1/planned-orders/action-messages/accept",
  operationId: "bulkAcceptPlannedOrderActionMessages",
  summary: "Accept many reschedule-out messages",
  description:
    "Spec 03 §3.8 and §5 row 49 (D-389 ruling 10). " +
    ORDER_MANAGE +
    " The body is a JSON array of `{id, expected_message}`, at most 200. Each id is applied in its own transaction, in id order, as the single accept applies a `reschedule_out`, and answers per id `accepted` or its refusal: 404 `PLANNED_ORDER_NOT_FOUND`, 409 `PLANNED_ORDER_NOT_FIRMED`, 409 `NO_ACTION_MESSAGE`, 409 `ACTION_MESSAGE_CHANGED` with `current_message`, 409 `ACTION_MESSAGE_NOT_BULK_ACCEPTABLE` for any other kind, which needs its own confirmation. Refusals of the whole request in order: 403, 400 (an empty array, more than 200 items, an id twice, a malformed item).",
  request: {
    body: { content: { "application/json": { schema: ActionMessageBulkAcceptRequest } } },
  },
  responses: {
    200: json("Per id, its outcome", PlannedOrderBulkResponse),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
  },
});

// --- P3-I-2: the setup matrix and the schedule (rows 54 to 66) ------------------------

const MATRIX_READ =
  "Gated by `planning.calendar.read`, held by group_admin, facility_admin, planner and maintenance; the matrix reads and writes on the calendar pair because §7's calendars row names exactly its holders (D-400 ruling 5, D-406 ruling 1).";
const SCHEDULE_READ =
  "Gated by `planning.schedule.read`, held by group_admin, facility_admin, planner, sales, procurement, finance, qc, maintenance and storekeeper (D-406 ruling 1). Every reader is served each operation's product, quantity, step, work centre and times; a work order's link opens only when module 04's door opens it for the reader, a pegged order's sales line is labelled through module 09's doors, and a planned order's number reads null (\"Planned order\") unless the reader's lineage reads that kind at the facility (spec 03 §7 narrowing (a)).";
const SCHEDULE_PUBLISH =
  "Gated by `planning.schedule.publish`, held by group_admin, facility_admin and planner (D-406 ruling 1).";

route({
  method: "get",
  path: "/v1/setup-matrix",
  operationId: "getSetupMatrix",
  summary: "A work centre's setup matrix",
  description:
    "Spec 03 §2.15 and §5 row 54 (D-400). " +
    MATRIX_READ +
    " Rows are sorted by facet, from value and to value. `facets_in_use` lists the facets and values the work centre's active routing steps carry, and a row whose values no such key uses reads `unused: true`, so the screen can mark it. Refusals in order: 403, 400 (no `facility_id` or `work_center_id`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` (also a work centre at another facility).",
  request: { query: SetupMatrixQuery },
  responses: {
    200: json("The work centre's matrix", SetupMatrix),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/setup-matrix/explain",
  operationId: "explainSetupChangeover",
  summary: "The changeover between two setup keys on a work centre",
  description:
    "Spec 03 §2.15, §4.6 and §4.8 (D-400 ruling 3; decided in P3-I-2's build). " +
    MATRIX_READ +
    " The scheduling kernel's own answer for a change from `from_key` to `to_key` at `static_minutes`, priced on the work centre's stored matrix, with the facets that differ and the row that priced each, so the matrix screen's test panel and the board's block drawer show exactly what the solver charges. Refusals in order: 403, 400 (no `facility_id` or `work_center_id`, a key over 512 characters, a malformed `static_minutes`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND`.",
  request: { query: SetupMatrixExplainQuery },
  responses: {
    200: json("The changeover and how it was priced", SetupMatrixExplanation),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/setup-matrix/{workCenterId}",
  operationId: "putSetupMatrix",
  summary: "Replace a work centre's setup matrix",
  description:
    "Spec 03 §2.15 and §5 row 55 (D-400 rulings 2 to 5). Gated by `planning.calendar.manage`, held by group_admin and facility_admin. Replaces every row of the work centre under a per-work-centre advisory lock, with `expected_updated_at` the latest `updated_at` the caller read, null for none. Arms the facility's solve request. Appends `setup_matrix.replaced` with the counts added, removed and changed. Refusals in order: 403, 404 `WORK_CENTER_NOT_FOUND` (also archived or outside the caller's scope), 400 (the body's shape, more than 2 000 rows), 409 `SETUP_MATRIX_CHANGED` with the current `updated_at`, 422 `SETUP_MATRIX_INVALID` with `rows [{index, reasons}]`, the reasons `facet_malformed`, `value_malformed`, `same_value`, `duplicate_row` and `minutes_out_of_range`.",
  request: {
    params: SetupMatrixWorkCenterParam,
    body: { content: { "application/json": { schema: SetupMatrixPutRequest } } },
  },
  responses: {
    200: json("The work centre's matrix as saved", SetupMatrix),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("WORK_CENTER_NOT_FOUND"),
    409: problemOf("SETUP_MATRIX_CHANGED with `updated_at`", SetupMatrixConflictProblem),
    422: problemOf("SETUP_MATRIX_INVALID with `rows`", SetupMatrixInvalidProblem),
  },
});

route({
  method: "post",
  path: "/v1/schedules/solve",
  operationId: "requestScheduleSolve",
  summary: "Ask for a solve at a facility",
  description:
    "Spec 03 §3.5 and §5 row 56 (D-398 rulings 2 and 3). " +
    SCHEDULE_PUBLISH +
    " Starts a manual solve, whose draft waits for a person to publish it. While a solve runs at the facility, the request is armed due now with the caller as `manual_requested_by`, and the answer is the running run with `queued: true`. Appends `schedule.solve_requested`. Refusals in order: 403, 400 (no `facility_id`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { body: { content: { "application/json": { schema: ScheduleSolveRequest } } } },
  responses: {
    202: json("The run started, or the running one with the request queued", ScheduleSolveAccepted),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedule-runs",
  operationId: "listScheduleRuns",
  summary: "A facility's solve history",
  description:
    "Spec 03 §2.19 and §5 row 57 (D-398 ruling 5). " +
    SCHEDULE_READ +
    " Newest first, cursor keyset, `limit` 1 to 200, default 50. `pending_request` is the facility's armed request, and `consecutive_failures` counts the solves that ended `failed` since the last success. `solves_only=true` serves the solves alone (`cron`, `event`, `manual`, `commit_move` and `apply_move`), so neither a `ctp_probe` nor a `drag_preview` run reads as the facility's last solve (D-461 ruling 11). Refusals in order: 403, 400 (no `facility_id`, an unknown `status` or `trigger`, a `solves_only` other than `true` or `false`, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: ScheduleRunListQuery },
  responses: {
    200: json("One page of runs", ScheduleRunListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedule-runs/{id}",
  operationId: "getScheduleRun",
  summary: "One solve run with its stats",
  description:
    "Spec 03 §2.19 and §5 row 58. " +
    SCHEDULE_READ +
    " Refusals in order: 403, 404 `SCHEDULE_RUN_NOT_FOUND` (also a run outside the caller's scope).",
  request: { params: ScheduleRunIdParam },
  responses: {
    200: json("The run", ScheduleRun),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_RUN_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedules",
  operationId: "listSchedules",
  summary: "A facility's schedule versions",
  description:
    "Spec 03 §2.16 and §5 row 59 (D-398 ruling 6). " +
    SCHEDULE_READ +
    " Headers newest first, cursor keyset, `limit` 1 to 200, default 50. A purged version is gone; its `schedule.published` audit row keeps its number, time and author. Refusals in order: 403, 400 (no `facility_id`, an unknown `status`, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: ScheduleListQuery },
  responses: {
    200: json("One page of schedule headers", ScheduleListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedules/live",
  operationId: "getLiveSchedule",
  summary: "The published schedule and the waiting draft",
  description:
    "Spec 03 §4.6 and §5 row 60 (D-399, D-404). " +
    SCHEDULE_READ +
    " `schedule` is null before the facility's first publish. Lanes carry their working pieces in the window and the two maintenance ports' answers, `source: unbound` until module 08 binds them. Operations page by work centre, sequence and reference, and the lanes and the unplaced list come on the first page alone. A cursor names the schedule it was cut from: a later page reads that schedule only, so a client follows `next_cursor` through row 61 with the first page's `schedule.id`, and a cursor here whose schedule is no longer the published one is refused 409 `SCHEDULE_SUPERSEDED` and the read starts again (D-411 ruling 5). Refusals in order: 403, 400 (no `facility_id`, a window over 84 days or reversed, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND` for a `work_center_id` not at the facility, 409 `SCHEDULE_SUPERSEDED`.",
  request: { query: ScheduleLiveQuery },
  responses: {
    200: json("The board", ScheduleBoard),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
    409: problemResponse("SCHEDULE_SUPERSEDED"),
  },
});

route({
  method: "get",
  path: "/v1/schedules/{id}",
  operationId: "getSchedule",
  summary: "Any schedule version as a board",
  description:
    "Spec 03 §5 row 61. " +
    SCHEDULE_READ +
    " The board of row 60 for the version asked for, with the facility's waiting draft beside it when that is another version. A cursor cut from another version is malformed (D-411 ruling 5). Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND` (also a version outside the caller's scope), 400 (a window over 84 days or reversed, a malformed cursor, a `work_center_id` that is not a uuid).",
  request: { params: ScheduleIdParam, query: ScheduleBoardQuery },
  responses: {
    200: json("The version's board", ScheduleBoard),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND / WORK_CENTER_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedules/{id}/diff",
  operationId: "getScheduleDiff",
  summary: "A schedule version against its base",
  description:
    "Spec 03 §4.6 and §5 row 62 (D-399 ruling 2). " +
    SCHEDULE_READ +
    " Each operation present in both whose start moved by a minute or more or whose lane changed, and the overloads new to the version and cleared by it, each list at most 200 beside its exact count, with the version's auto-publish verdict. A first schedule, or one whose base was purged, reads every list empty. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`.",
  request: { params: ScheduleIdParam },
  responses: {
    200: json("The diff", ScheduleDiff),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/schedules/{id}/publish",
  operationId: "publishSchedule",
  summary: "Publish a draft schedule",
  description:
    "Spec 03 §3.5 and §5 row 63 (D-399 rulings 1 to 3). " +
    SCHEDULE_PUBLISH +
    " Under the `schedule` lock, the draft becomes the facility's published schedule, the previous one is superseded with its open overloads, every other draft is discarded, and module 04's `OperationScheduleWriter` writes the floor's scheduled starts in the same transaction. Emits `planning.schedule_published` and `planning.capacity_overloaded` for each overload new against the superseded schedule. Appends `schedule.published`. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 400 (a body that is not empty), 409 `SCHEDULE_NOT_DRAFT` with `schedule_status` and `newest_draft_id`, null when no draft waits, 409 `SCHEDULE_SUPERSEDED` (its base is no longer the published schedule).",
  request: {
    params: ScheduleIdParam,
    body: { content: { "application/json": { schema: ScheduleDecisionRequest } } },
  },
  responses: {
    200: json("The published header", ScheduleHeader),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND"),
    409: problemOf(
      "SCHEDULE_NOT_DRAFT with `schedule_status` and `newest_draft_id`, or SCHEDULE_SUPERSEDED",
      ScheduleConflictProblem,
    ),
  },
});

route({
  method: "post",
  path: "/v1/schedules/{id}/discard",
  operationId: "discardSchedule",
  summary: "Discard a draft schedule",
  description:
    "Spec 03 §2.16 and §5 row 64. " +
    SCHEDULE_PUBLISH +
    " The draft turns `discarded` and the nightly retention deletes it after 24 hours. Appends `schedule.discarded`. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 400 (a body that is not empty), 409 `SCHEDULE_NOT_DRAFT` with `schedule_status` and `newest_draft_id`.",
  request: {
    params: ScheduleIdParam,
    body: { content: { "application/json": { schema: ScheduleDecisionRequest } } },
  },
  responses: {
    200: json("The discarded header", ScheduleHeader),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND"),
    409: problemOf(
      "SCHEDULE_NOT_DRAFT with `schedule_status` and `newest_draft_id`",
      ScheduleConflictProblem,
    ),
  },
});

route({
  method: "get",
  path: "/v1/capacity/load",
  operationId: "getCapacityLoad",
  summary: "Load against capacity per work centre and day or week",
  description:
    "Spec 03 §2.19a, §4.7 and §5 row 65 (D-401). " +
    SCHEDULE_READ +
    " Reads the stored load of the published schedule, or of `schedule_id`, between `from` and `to`, both inclusive and at most 84 days apart: capacity, placed setup and run, the due-date load and the rough-cut load of proposed planned orders, with each week's overload. A week row starts on the Monday and sums its dates. With no published schedule and none named, `schedule_id` is null and `rows` empty. Refusals in order: 403, 400 (no `facility_id`, `from`, `to` or `grain`, a window over 84 days or reversed), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `SCHEDULE_NOT_FOUND` (also a schedule of another facility).",
  request: { query: CapacityLoadQuery },
  responses: {
    200: json("The load rows", CapacityLoad),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / SCHEDULE_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/schedules/{id}/overloads",
  operationId: "listScheduleOverloads",
  summary: "A schedule's overloads",
  description:
    "Spec 03 §2.18 and §5 row 66 (D-401 rulings 2 and 3). " +
    SCHEDULE_READ +
    " Each overloaded work centre and week with its hours, peak date and the late and unplaced operations whose order is due in the week, at most 200 beside the exact count, ordered by week and work centre, cursor keyset, `limit` 1 to 200, default 50. From P3-I-3 each row carries its ranked moves, at most one of each kind, with `moves_status`, for a holder of `planning.schedule.publish` or `planning.cost.read` at the schedule's facility, and both keys are absent for every other reader; a move's `cost_minor`, `value_at_risk_minor`, `currency` and `unpriced_reason` are served to a holder of `planning.cost.read` and absent by key otherwise, the moves in the same order (spec 03 §7 narrowing (b), D-402 ruling 6). An applied overload names its move, author and time to every reader. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 400 (a `week_start` that is not a Monday, an unknown `status`, a malformed cursor).",
  request: { params: ScheduleIdParam, query: ScheduleOverloadListQuery },
  responses: {
    200: json("One page of overloads", ScheduleOverloadListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND"),
  },
});

// --- P3-I-3: moves, the drag and the directives (rows 67 to 71) ---------------------

route({
  method: "post",
  path: "/v1/schedules/{id}/overloads/{overloadId}/apply-move",
  operationId: "applyScheduleMove",
  summary: "Apply an overload's move and publish",
  description:
    "Spec 03 §3.5 and §5 row 67 (D-402 ruling 5). " +
    SCHEDULE_PUBLISH +
    " Under the `schedule` lock, on the facility's published schedule, writes the move into the factory's data: an overtime move inserts its `calendar_exceptions` rows (`extra_shift`, factor 1.00, the applier as author), an alternate one `pin_work_center` directive per moved operation, and a push one `defer` directive. It then solves once with trigger `apply_move` inside the request, scores nothing, publishes the result and answers its header. The overload turns `move_applied` with the move, its author and time. Applying an overtime move also needs `planning.calendar.manage` and `planning.cost.read` at the facility. Appends `schedule_overload.move_applied` with the ids it wrote. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 404 `SCHEDULE_OVERLOAD_NOT_FOUND`, 400 (the body's shape), 409 `MOVE_ALREADY_APPLIED` (the overload was answered; checked first because every apply publishes and so supersedes the overload's schedule), 409 `SCHEDULE_SUPERSEDED` (not the published schedule), 422 `MOVE_NOT_FOUND` (an id the overload does not list), 403 `PERMISSION_DENIED` with `required_codes` (an overtime move without the calendar and cost codes; nothing is written), 422 `MOVE_NOT_APPLICABLE` with `reason` (`order_closed`, `work_center_archived`, `operation_done`, `operation_started`, `operation_held`, `overtime_past`).",
  request: {
    params: ScheduleOverloadParams,
    body: { content: { "application/json": { schema: ScheduleApplyMoveRequest } } },
  },
  responses: {
    200: json("The newly published schedule's header", ScheduleMoveResult),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemOf(
      "PERMISSION_DENIED, with `required_codes` for an overtime move",
      ScheduleApplyMoveForbiddenProblem,
    ),
    404: problemResponse("SCHEDULE_NOT_FOUND / SCHEDULE_OVERLOAD_NOT_FOUND"),
    409: problemOf("MOVE_ALREADY_APPLIED / SCHEDULE_SUPERSEDED", ScheduleConflictProblem),
    422: problemOf(
      "MOVE_NOT_FOUND, or MOVE_NOT_APPLICABLE with `reason`",
      ScheduleMoveNotApplicableProblem,
    ),
  },
});

const DRAG_REFUSALS =
  "422 `OP_IN_PROGRESS` (a started operation), 422 `OP_NOT_MOVABLE` (an operation on quality hold or outsourced, which holds no lane), 422 `MOVE_IN_PAST` (a start before now), 422 `WORK_CENTER_NOT_ALLOWED` (neither the step's work centre nor one of its alternates), 422 `PIN_OVERLAPS_FIXED_WORK` (a block over an in-progress hold or another active pin on that work centre)";

route({
  method: "post",
  path: "/v1/schedules/{id}/preview-move",
  operationId: "previewScheduleMove",
  summary: "Preview a drag's consequences",
  description:
    "Spec 03 §3.6 and §5 row 68 (D-403 rulings 3 and 4). " +
    SCHEDULE_PUBLISH +
    " On the facility's published schedule, rebuilds the solve's input as the solve service reads it now, adds the candidate as a `pin_position` replacing any directive of that operation, solves in the request without persisting a schedule, and writes one `schedule_runs` row with trigger `drag_preview` recording the consequences. Answers the diff against the published schedule: the operations that move, the orders newly late and no longer late, the KPI delta, the new overloads and any precedence conflict, each list at most 200 beside its count, with `preview_run_id`. `op_ref` is the operation's reference as the board served it, `op:<id>` included. Labels follow §7. Writes no audit row. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 400 (the body's shape, a start not on a whole minute or without an offset), 409 `SCHEDULE_SUPERSEDED` (not the published schedule), 404 `SCHEDULE_OPERATION_NOT_FOUND`, " +
    DRAG_REFUSALS +
    ".",
  request: {
    params: ScheduleIdParam,
    body: { content: { "application/json": { schema: ScheduleMovePreviewRequest } } },
  },
  responses: {
    200: json("The drag's consequences", ScheduleMovePreview),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_NOT_FOUND / SCHEDULE_OPERATION_NOT_FOUND"),
    409: problemOf("SCHEDULE_SUPERSEDED", ScheduleConflictProblem),
    422: problemResponse(
      "OP_IN_PROGRESS / OP_NOT_MOVABLE / MOVE_IN_PAST / WORK_CENTER_NOT_ALLOWED / PIN_OVERLAPS_FIXED_WORK",
    ),
  },
});

route({
  method: "post",
  path: "/v1/schedules/{id}/commit-move",
  operationId: "commitScheduleMove",
  summary: "Commit a previewed drag and publish",
  description:
    "Spec 03 §3.6 and §5 row 69 (D-403 ruling 4). " +
    SCHEDULE_PUBLISH +
    " Re-solves at the commit's own time on the facts read then, diffs against the schedule published now, which may be newer than the preview's, and compares the consequences with the preview's record: they match when the same orders are newly late, none slips more days than the preview said, no overload is new that the preview did not list, and every moved operation starts within 60 minutes of its previewed start. A preview older than 24 hours is stale whatever it says. On a match it inserts the `pin_position` directive, writes the solve as a new version with trigger `commit_move`, publishes it and answers its header. A schedule superseded since the preview is not refused. A body without `work_center_id` commits the lane the preview resolved. The commit spends its preview run, so the same `preview_run_id` never commits twice. Appends `schedule.move_committed` with the operation's old and new start and work centre and the directive. Refusals in order: 403, 404 `SCHEDULE_NOT_FOUND`, 400 (the body's shape), 404 `SCHEDULE_RUN_NOT_FOUND` (no uncommitted preview of this body on this schedule), 404 `SCHEDULE_OPERATION_NOT_FOUND`, 409 `SCHEDULE_SUPERSEDED` (the facility has no published schedule now), " +
    DRAG_REFUSALS +
    ", 409 `PREVIEW_STALE` with the fresh `preview` and the `reasons` the old one failed, 422 `MOVE_BREAKS_PRECEDENCE` with `precedence_conflicts`.",
  request: {
    params: ScheduleIdParam,
    body: { content: { "application/json": { schema: ScheduleMoveCommitRequest } } },
  },
  responses: {
    200: json("The newly published schedule's header", ScheduleMoveResult),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse(
      "SCHEDULE_NOT_FOUND / SCHEDULE_RUN_NOT_FOUND / SCHEDULE_OPERATION_NOT_FOUND",
    ),
    409: problemOf(
      "PREVIEW_STALE with the fresh `preview` and `reasons` / SCHEDULE_SUPERSEDED",
      SchedulePreviewStaleProblem,
    ),
    422: problemOf(
      "OP_IN_PROGRESS / OP_NOT_MOVABLE / MOVE_IN_PAST / WORK_CENTER_NOT_ALLOWED / PIN_OVERLAPS_FIXED_WORK / MOVE_BREAKS_PRECEDENCE with `precedence_conflicts`",
      ScheduleMoveBreaksPrecedenceProblem,
    ),
  },
});

route({
  method: "get",
  path: "/v1/schedule-directives",
  operationId: "listScheduleDirectives",
  summary: "A facility's pins and defers",
  description:
    "Spec 03 §2.19b and §5 row 70 (D-403). " +
    SCHEDULE_READ +
    " The facility's directives with `status` (`active` when omitted) and `kind`, newest first, cursor keyset, `limit` 1 to 200, default 50. Each carries its author's id and no words; its operation and order references are served where its label's link opens for the reader and are null otherwise (§7 narrowings (a) and (c)). Refusals in order: 403, 400 (no `facility_id`, an unknown `status` or `kind`, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: ScheduleDirectiveListQuery },
  responses: {
    200: json("One page of directives", ScheduleDirectiveListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "delete",
  path: "/v1/schedule-directives/{id}",
  operationId: "removeScheduleDirective",
  summary: "Remove a pin or a defer",
  description:
    "Spec 03 §3.6 and §5 row 71 (D-403 ruling 5). " +
    SCHEDULE_PUBLISH +
    " The directive turns `removed` with `unpinned`, the caller and the time, and the facility's solve request is armed; nothing is published by the removal itself. Appends `schedule_directive.removed`. Refusals in order: 403, 404 `SCHEDULE_DIRECTIVE_NOT_FOUND` (also another facility's outside the caller's scope), 409 `SCHEDULE_DIRECTIVE_NOT_ACTIVE` with `directive_status`.",
  request: { params: ScheduleDirectiveIdParam },
  responses: {
    204: { description: "Removed" },
    400: problemResponse("IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCHEDULE_DIRECTIVE_NOT_FOUND"),
    409: problemOf(
      "SCHEDULE_DIRECTIVE_NOT_ACTIVE with `directive_status`",
      ScheduleDirectiveConflictProblem,
    ),
  },
});

// --- P3-J J-1: promises (rows 74 to 78) ---------------------------------------------

const PROMISE_COMMIT =
  "Gated by `planning.promise.commit`, held by group_admin, facility_admin, planner and sales (D-464 ruling 1).";
const PROMISE_READ =
  "Gated by `planning.read`, held by group_admin, facility_admin, planner, sales, procurement and finance (D-464 ruling 4). The customer and the order are labelled through module 09's doors for the reader, and no promise carries money (D-464 ruling 5).";

route({
  method: "post",
  path: "/v1/promises/quote",
  operationId: "quotePromise",
  summary: "Quote a capable-to-promise date",
  description:
    "Spec 03 §3.7 as built and §5 row 74 (D-461, D-462 ruling 1). " +
    PROMISE_COMMIT +
    " Off-cuts first when `allow_offcuts` (default true) and the product is sold by the cut, then cumulative ATP over on-hand stock, receipts and every live promise at the facility, then a two-pass capacity probe that never moves existing work, which writes one `ctp_probe` run. `qty` is in the product's base unit. The answer is a `quoted` promise valid for 72 hours that holds nothing, with `promised_date = max(requested_date, earliest_date)`. Appends `promise.quoted`. Refusals in order: 403, 400, 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `PRODUCT_NOT_FOUND`, 422 `PRODUCT_ARCHIVED`, 404 `ORDER_LINE_NOT_FOUND` (also a line module 09's order door does not open to the caller), 409 `ORDER_LINE_NOT_PROMISABLE` with `reason` `facility_mismatch` or `product_mismatch` for a line of another plant or product, 429 `CTP_BUSY` with `Retry-After: 2` when two probes already run at the facility on this task, 422 `CAPACITY_PATH_UNAVAILABLE` with `reasons` and `material` (no promise is written). The line is never counted as demand against itself.",
  request: { body: { content: { "application/json": { schema: CtpQuoteRequest } } } },
  responses: {
    201: json("The quoted promise", CtpQuoteResult),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND / PRODUCT_NOT_FOUND / ORDER_LINE_NOT_FOUND"),
    409: problemOf("ORDER_LINE_NOT_PROMISABLE", CtpCommitConflictProblem),
    422: problemOf("PRODUCT_ARCHIVED / CAPACITY_PATH_UNAVAILABLE", CtpUnavailableProblem),
    429: problemResponse("CTP_BUSY"),
  },
});

route({
  method: "post",
  path: "/v1/promises/{id}/commit",
  operationId: "commitPromise",
  summary: "Commit a quoted promise to a sales order line",
  description:
    "Spec 03 §3.7 as built, paragraph 6, and §5 row 75 (D-462 rulings 2 to 4). " +
    PROMISE_COMMIT +
    " A fresh probe runs with the promise's inputs; then one transaction checks each cited off-cut unit under module 06's unit lock, reserves the free ones for the line, flips the status to `committed`, stores `reserved_reservation_ids`, emits `planning.promise_committed` and appends `promise.committed`. A full-length stock promise reserves nothing: every later probe counts it as demand. The line must sit on a confirmed order and belong to the promise's customer, product and facility with at least its quantity; it is read again, held `FOR SHARE` with its order, inside the commit's transaction. Commits run one at a time per tenant, and one that finds another landed after its probe probes again. Refusals in order: 403, 404 `PROMISE_NOT_FOUND`, 400, 409 `PROMISE_NOT_QUOTED`, 409 `PROMISE_EXPIRED`, 422 `ORDER_LINE_REQUIRED`, 404 `ORDER_LINE_NOT_FOUND` (also a line module 09's order door does not open to the caller), 409 `ORDER_LINE_NOT_PROMISABLE` with `reason` (a `pending_approval` order included), 409 `LINE_ALREADY_PROMISED` with `promise_id`, 429 `CTP_BUSY` from the fresh probe, 409 `BASIS_STALE` with `fresh_promise` and `stale_reasons`, a new quoted promise written in the same request, which a replay answers again with the same reasons rather than writing a second, or 422 `CAPACITY_PATH_UNAVAILABLE` when the fresh probe finds no date at all.",
  request: {
    params: CtpPromiseIdParam,
    body: { content: { "application/json": { schema: CtpCommitRequest } } },
  },
  responses: {
    200: json("The committed promise", CtpPromise),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PROMISE_NOT_FOUND / ORDER_LINE_NOT_FOUND"),
    409: problemOf(
      "PROMISE_NOT_QUOTED / PROMISE_EXPIRED / ORDER_LINE_NOT_PROMISABLE / LINE_ALREADY_PROMISED / BASIS_STALE",
      CtpCommitConflictProblem,
    ),
    422: problemOf("ORDER_LINE_REQUIRED / CAPACITY_PATH_UNAVAILABLE", CtpUnavailableProblem),
    429: problemResponse("CTP_BUSY"),
  },
});

route({
  method: "post",
  path: "/v1/promises/{id}/withdraw",
  operationId: "withdrawPromise",
  summary: "Withdraw a quoted promise",
  description:
    "Spec 03 §5 row 76 (D-464 ruling 2). " +
    PROMISE_COMMIT +
    " A `quoted` promise turns `withdrawn` with the reason `manual` and the caller's words, releasing nothing, emits `planning.promise_withdrawn` and appends `promise.withdrawn`. A committed or at-risk promise is what a customer was told, so its withdraw also needs `sales.order.re_promise` at the promise's facility (D-464 ruling 2): it turns `withdrawn` with `manual`, releases the holds its commit minted, and the line keeps its date. Refusals in order: 403, 404 `PROMISE_NOT_FOUND`, 400 (`reason` 1 to 500 characters), 409 `PROMISE_NOT_WITHDRAWABLE` with `promise_status` (kept, broken, withdrawn or expired), 403 `PERMISSION_DENIED` naming `sales.order.re_promise`.",
  request: {
    params: CtpPromiseIdParam,
    body: { content: { "application/json": { schema: CtpWithdrawRequest } } },
  },
  responses: {
    200: json("The withdrawn promise", CtpPromise),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PROMISE_NOT_FOUND"),
    409: problemOf("PROMISE_NOT_WITHDRAWABLE", CtpWithdrawConflictProblem),
  },
});

route({
  method: "get",
  path: "/v1/promises",
  operationId: "listPromises",
  summary: "A facility's promises",
  description:
    "Spec 03 §4.9 and §5 row 77 (D-463, D-464). " +
    PROMISE_READ +
    " Newest promised date first, cursor keyset, `limit` 1 to 200, default 50. `status` repeats; without it the list serves `quoted`, `committed` and `at_risk`. Refusals in order: 403, 400 (no `facility_id`, an unknown filter value, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: CtpPromiseListQuery },
  responses: {
    200: json("One page of promises", CtpPromiseListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/promises/{id}",
  operationId: "getPromise",
  summary: "One promise with its basis and timeline",
  description:
    "Spec 03 §4.9 and §5 row 78 (D-463). " +
    PROMISE_READ +
    " The promise with its labels, `basis_verdicts`, `verdict` and `timeline`. Refusals in order: 403, 404 `PROMISE_NOT_FOUND` (also one outside the caller's scope).",
  request: { params: CtpPromiseIdParam },
  responses: {
    200: json("The promise", CtpPromiseDetail),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("PROMISE_NOT_FOUND"),
  },
});

// --- P3-J J-4: the what-if sandbox (rows 80 to 85) ----------------------------------

const SCENARIO_MANAGE =
  "Gated by `planning.scenario.manage`, held by group_admin, facility_admin and planner (D-464 ruling 3). `kpi_diff.cost_impact` is served only to a holder of `planning.cost.read` at the facility and is absent by key otherwise; slipped and recovered orders are labelled as the board labels them for the reader (D-472 ruling 2).";

route({
  method: "post",
  path: "/v1/scenarios",
  operationId: "createScenario",
  summary: "Ask a what-if question",
  description:
    "Spec 03 §3.9 as built and §5 row 80 (D-471, D-472 ruling 1, D-473 ruling 1). " +
    SCENARIO_MANAGE +
    " Writes the scenario `open` with its 1 to 20 hypotheses, immutable from here, based on the facility's published schedule; the planning worker claims it, solves the live snapshot with the hypotheses applied to that input alone into a `kind = 'sandbox'` schedule, and moves it to `solved` or `failed`. Appends `scenario.created`. Refusals in order: 403, 400 (the body; a `machine_id` on `machine_down`), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`, 404 `WORK_CENTER_NOT_FOUND`, `PRODUCT_NOT_FOUND` or `ORDER_LINE_NOT_FOUND` with `hypothesis_index`, 409 `NO_PUBLISHED_SCHEDULE`, 409 `SCENARIO_LIMIT_REACHED` with `limit` (20 scenarios neither promoted nor discarded at the facility, or 5 open and solved ones an agent created there).",
  request: { body: { content: { "application/json": { schema: ScenarioCreateRequest } } } },
  responses: {
    202: json("The scenario, open and waiting for the worker", ScenarioCreateAccepted),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemOf(
      "FACILITY_NOT_FOUND / WORK_CENTER_NOT_FOUND / PRODUCT_NOT_FOUND / ORDER_LINE_NOT_FOUND",
      ScenarioProblem,
    ),
    409: problemOf("NO_PUBLISHED_SCHEDULE / SCENARIO_LIMIT_REACHED", ScenarioProblem),
  },
});

route({
  method: "get",
  path: "/v1/scenarios",
  operationId: "listScenarios",
  summary: "A facility's what-if scenarios",
  description:
    "Spec 03 §4.10 and §5 row 81. " +
    SCENARIO_MANAGE +
    " Newest first, cursor keyset, `limit` 1 to 200, default 50, each row with its late-orders delta once solved. Refusals in order: 403, 400 (no `facility_id`, an unknown status, a malformed cursor), 403 for a facility outside the caller's scope, 404 `FACILITY_NOT_FOUND`.",
  request: { query: ScenarioListQuery },
  responses: {
    200: json("One page of scenarios", ScenarioListResponse),
    400: problemResponse("VALIDATION_FAILED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("FACILITY_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/scenarios/{id}",
  operationId: "getScenario",
  summary: "One scenario with its hypotheses and KPI diff",
  description:
    "Spec 03 §4.10 and §5 row 82 (D-471 ruling 6, D-472 ruling 2). " +
    SCENARIO_MANAGE +
    " The scenario with each hypothesis and whether promote would write it, its sandbox schedule (whose lanes row 61 serves), `base_superseded` and `kpi_diff`. The read moves `last_touched_at`, which the 14-day auto-discard reads. Refusals in order: 403, 404 `SCENARIO_NOT_FOUND` (also one outside the caller's scope).",
  request: { params: ScenarioIdParam },
  responses: {
    200: json("The scenario", Scenario),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCENARIO_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/scenarios/{id}/solve",
  operationId: "solveScenario",
  summary: "Solve a scenario again on the newest published schedule",
  description:
    "Spec 03 §5 row 83 (D-471 rulings 4 and 5). " +
    SCENARIO_MANAGE +
    " Moves a `solved` or `failed` scenario back to `open`, rebased on the facility's newest published schedule, for the worker to solve again with the same hypotheses; the earlier sandbox and run go when the new solve writes. Appends `scenario.solve_requested`. Refusals in order: 403, 404 `SCENARIO_NOT_FOUND`, 400, 409 `SCENARIO_NOT_SOLVABLE` with `scenario_status`, 409 `NO_PUBLISHED_SCHEDULE`.",
  request: {
    params: ScenarioIdParam,
    body: { content: { "application/json": { schema: ScenarioActionRequest } } },
  },
  responses: {
    202: json("The scenario, open again", Scenario),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCENARIO_NOT_FOUND"),
    409: problemOf("SCENARIO_NOT_SOLVABLE / NO_PUBLISHED_SCHEDULE", ScenarioProblem),
  },
});

route({
  method: "post",
  path: "/v1/scenarios/{id}/promote",
  operationId: "promoteScenario",
  summary: "Write a solved scenario's persistable hypotheses to the live calendar",
  description:
    "Spec 03 §3.9 as built and §5 row 84 (D-472 ruling 3). Gated by `planning.scenario.promote`, held by group_admin and facility_admin, and also needs `planning.calendar.manage` and `planning.cost.read` at the facility. Each `add_shift` writes one `extra_shift` exception through the calendar service, `source = 'manual'`, with its own `calendar_exception.created` audit row; each `change_efficiency` clones the work centre's pattern as `<code>-S<4 hex>` with that shift's efficiency, ends the work centre's assignment the day before the facility's next working day and assigns the clone from that day. `machine_down`, `add_order` and `drop_order` are not written and are listed under `not_applied`. Then arms an urgent live solve, emits `planning.scenario_promoted`, appends `scenario.promoted` and moves the scenario to `promoted`. Refusals in order: 403, 404 `SCENARIO_NOT_FOUND`, 400, 403 `PERMISSION_DENIED` with `required_codes`, 409 `SCENARIO_NOT_SOLVED` with `scenario_status`, 422 `NOT_PERSISTABLE` when no hypothesis can be written.",
  request: {
    params: ScenarioIdParam,
    body: { content: { "application/json": { schema: ScenarioActionRequest } } },
  },
  responses: {
    200: json("The promoted scenario and what it wrote", ScenarioPromoteResult),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemOf("PERMISSION_DENIED", ScenarioProblem),
    404: problemResponse("SCENARIO_NOT_FOUND"),
    409: problemOf("SCENARIO_NOT_SOLVED", ScenarioProblem),
    422: problemResponse("NOT_PERSISTABLE"),
  },
});

route({
  method: "post",
  path: "/v1/scenarios/{id}/discard",
  operationId: "discardScenario",
  summary: "Discard a scenario",
  description:
    "Spec 03 §5 row 85 (D-471 ruling 6). " +
    SCENARIO_MANAGE +
    " Moves an `open`, `solved` or `failed` scenario to `discarded`; its sandbox schedule, operations, load and run are purged with it 30 days later. Appends `scenario.discarded`. Refusals in order: 403, 404 `SCENARIO_NOT_FOUND`, 400, 409 `SCENARIO_CLOSED` with `scenario_status` (promoted or discarded).",
  request: {
    params: ScenarioIdParam,
    body: { content: { "application/json": { schema: ScenarioActionRequest } } },
  },
  responses: {
    200: json("The discarded scenario", Scenario),
    400: problemResponse("VALIDATION_FAILED / IDEMPOTENCY_KEY_REQUIRED"),
    403: problemResponse("PERMISSION_DENIED"),
    404: problemResponse("SCENARIO_NOT_FOUND"),
    409: problemOf("SCENARIO_CLOSED", ScenarioProblem),
  },
});
